import { type FormEvent, useEffect, useRef, useState } from 'react';
import shellHtml from './generated/legacy-shell.html?raw';
import { mountLegacyApp } from './generated/legacy-runtime.js';
import { api, type SessionUser } from './lib/api';
import { launchPlanClient } from './lib/launch-plan-client';
import { mountWaveReportPackaging } from './wave-report-packaging';
import { mountCategoryPriceMap } from './category-price-map';
import { mountPricingDistributionAdjustment } from './pricing-distribution-adjustment';
import { mountAgeStyleAdjustment } from './age-style-adjustment';
import { mountCatIpAdjustment } from './cat-ip-adjustment';
import './app.css';

export function App() {
  const mounted = useRef(false);
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'anonymous'>('checking');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getSession().then(session => {
      setUser(session.user);
      setAuthState(session.authenticated ? 'authenticated' : 'anonymous');
    }).catch(reason => {
      setAuthError(reason instanceof Error ? `无法连接服务器：${reason.message}` : '无法连接服务器');
      setAuthState('anonymous');
    });
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      mounted.current = false;
      setUser(null);
      setAuthState('anonymous');
      setAuthError('登录已过期，当前编辑已保存在本机。重新登录后将继续同步。');
    };
    window.addEventListener('angelmyth:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('angelmyth:auth-expired', handleAuthExpired);
  }, []);

  useEffect(() => {
    if (authState !== 'authenticated') return;
    const keepAlive = () => {
      void api.getSession().then(session => {
        if (!session.authenticated) window.dispatchEvent(new CustomEvent('angelmyth:auth-expired'));
      }).catch(() => undefined);
    };
    const timer = window.setInterval(keepAlive, 6 * 60 * 60 * 1000);
    const onVisible = () => { if (!document.hidden) keepAlive(); };
    document.addEventListener('visibilitychange', onVisible);
    keepAlive();
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authState]);

  useEffect(() => {
    if (authState !== 'authenticated' || !user || mounted.current) return;
    mounted.current = true;
    launchPlanClient.setCurrentUser(user.id, user.role);
    void mountLegacyApp(user).then(() => { mountWaveReportPackaging(); mountCategoryPriceMap(); mountPricingDistributionAdjustment(); mountAgeStyleAdjustment(); mountCatIpAdjustment(); }).catch((reason: unknown) => {
      console.error(reason);
      setError(reason instanceof Error ? reason.message : '页面初始化失败');
    });
  }, [authState, user]);

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setAuthError(null);
    try {
      const result = await api.login(username.trim().toLowerCase(), password);
      mounted.current = false;
      setUser(result.user);
      setAuthState('authenticated');
    } catch (reason) {
      setAuthError(reason instanceof Error ? reason.message : '登录失败，请稍后重试');
    } finally { setSubmitting(false); }
  }

  if (authState === 'checking') return <div className="auth-loading" aria-label="正在连接品牌企划系统"><span /></div>;
  if (authState === 'anonymous') return <main className="auth-page"><section className="auth-card"><div className="auth-mark">AM</div><div className="auth-kicker">ANGELMYTH / BRAND DESIGN PLAN</div><h1>品牌企划协作系统</h1><p>01–11栏为团队共享企划内容；第12栏支持5位同事并行建立产品计划、场景与主题。本机内部访问会自动以创建者身份进入。</p><form onSubmit={handleLogin}><label htmlFor="username">用户名</label><input id="username" autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} placeholder="例如：angelmyth01" required autoFocus /><label htmlFor="teamPassword">密码</label><input id="teamPassword" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /><button type="submit" disabled={submitting}>{submitting ? '正在登录…' : '进入企划系统'}</button>{authError && <div className="auth-error" role="alert">{authError}</div>}</form><div className="auth-account-hint">创建者：angelmyth01 · 协作账号：colleague01–colleague05</div></section></main>;
  if (error) return <main className="startup-error"><h1>页面暂时无法加载</h1><p>{error}</p><button onClick={() => location.reload()}>重新加载</button></main>;
  return <div dangerouslySetInnerHTML={{ __html: shellHtml }} />;
}
